import {
  documentUploadsModule,
  formatUploadError,
  selFailedUploads,
  Upload
} from '@features/document-uploads'
import { WizardUIComponent } from '@features/formation-viewer/formation-wizard/wizard-ui-component'
import {
  selFolderSaveStatus,
  selResolvedInputs,
  selUploadedDocuments,
  stFolderModule
} from '@features/st-folder-viewer/st-folder-module'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  Dialog,
  DialogButtons,
  Modal,
  PostItNote,
  QuestionnaireBanner,
  QuestionnaireFooter,
  QuestionnaireHeader,
  QuestionnairePageContainer,
  QuestionnaireProgressBar,
  QuestionnaireSectionTab
} from '@st/theme'
import { Progress } from '@st/util/progress'
import { SimpleHtmlMarkdown } from '@util/simple-html-markdown'
import { useEffect, useState } from 'react'
import { match, P } from 'ts-pattern'
import { useProcess, useProcessState } from '~/store'
import { ClientUploadDocumentsCard } from './client-upload-documents-card'
import {
  selQuestionnaireCurrentPage,
  selQuestionnaireNavState,
  selQuestionnairePersonalizedNote,
  selQuestionnaireProgress,
  selQuestionnaireShouldShowSubtitle,
  selQuestionnaireTabs,
  selQuestionnaireValidationErrorMessage,
  stQuestionnaireModule
} from './st-questionnaire-module'
import { STQuestionnaireTrailingHeader } from './st-questionnaire-trailing-header'
import { QuestionnairePageValidationResult } from './st-questionnaire-validation'

type QuestionnaireAction = { type: 'viewAttachments' }

export function STQuestionnairePage() {
  const stQuestionnaire = useProcess(stQuestionnaireModule)
  const currentPage = useProcessState(stQuestionnaireModule, selQuestionnaireCurrentPage)!
  const navState = useProcessState(stQuestionnaireModule, selQuestionnaireNavState)!
  const progress = useProcessState(stQuestionnaireModule, selQuestionnaireProgress)
  const tabs = useProcessState(stQuestionnaireModule, selQuestionnaireTabs)
  const documentUploads = useProcess(documentUploadsModule)
  const failedUploads = useProcessState(documentUploadsModule, selFailedUploads)

  const personalizedNote = useProcessState(stQuestionnaireModule, selQuestionnairePersonalizedNote)

  const shouldShowSubtitle = useProcessState(
    stQuestionnaireModule,
    selQuestionnaireShouldShowSubtitle
  )
  const organization = useProcessState(stQuestionnaireModule, (s) => s.organization)

  const questionnaireValidationErrorMessage = useProcessState(
    stQuestionnaireModule,
    selQuestionnaireValidationErrorMessage
  )
  const questionnaireValidationResult = useProcessState(
    stQuestionnaireModule,
    (s): QuestionnairePageValidationResult => s.questionnaire.validationResult
  )

  const inputs = useProcessState(stFolderModule, selResolvedInputs)

  useEffect(() => {
    // we re-run the validation on every key-stroke to ensure that the validation
    // message stays up to date to avoid confusion
    if (!questionnaireValidationResult.ok) {
      stQuestionnaire.send({ type: 'rerunValidation' })
    }
  }, [inputs, questionnaireValidationResult.ok])

  const saveStatus = useProcessState(stFolderModule, selFolderSaveStatus)
  const documentsCount = useProcessState(stFolderModule, (s) => selUploadedDocuments(s).length)

  const [action, setAction] = useState<QuestionnaireAction | undefined>()

  const actionDialog = match(action)
    .with({ type: 'viewAttachments' }, () => (
      <Modal isOpen={true}>
        <ClientUploadDocumentsCard variant="dialog" onClose={() => setAction(undefined)} />
      </Modal>
    ))
    .otherwise(() => null)

  return (
    <QuestionnairePageContainer
      banner={
        questionnaireValidationErrorMessage && (
          <QuestionnaireBanner variant="error">
            {questionnaireValidationErrorMessage}
          </QuestionnaireBanner>
        )
      }
      header={
        <QuestionnaireHeader
          leading={<div className="text-md hidden font-semibold md:block">{organization.name}</div>}
          trailing={
            <STQuestionnaireTrailingHeader
              saveStatus={saveStatus}
              documentsCount={documentsCount}
              onClickViewAttachments={() => setAction({ type: 'viewAttachments' })}
              onClose={() => stQuestionnaire.send({ type: 'returnToDashboard' })}
            />
          }
        >
          {tabs.map((tab) => {
            return (
              <QuestionnaireSectionTab
                key={tab.route.sectionId}
                icon={tab.route.sectionId}
                caption={tab.label}
                selected={tab.route.sectionId == navState.cur.sectionId}
                onClick={() => stQuestionnaire.send({ type: 'navigate', route: tab.route })}
              />
            )
          })}
        </QuestionnaireHeader>
      }
      stickyNote={
        personalizedNote && (
          <div className="lg:sticky lg:left-6 lg:top-6 lg:h-0 lg:w-72">
            <PostItNote className="w-full" signature={organization.name!}>
              {personalizedNote!.note}
            </PostItNote>
          </div>
        )
      }
      footer={
        <QuestionnaireFooter>
          <Button
            size="l"
            variant="primary"
            leadingIcon={<ArrowLeftIcon className="h-4 w-4" />}
            disabled={navState.prev == undefined}
            onClick={() => stQuestionnaire.send({ type: 'navigate', route: navState.prev! })}
          >
            Back
          </Button>
          <QuestionnaireProgressBar value={Progress.toNumber(progress)} />
          {match(navState.next)
            .with(P.nullish, () => (
              <Button
                size="l"
                variant="primary"
                onClick={() => {
                  stQuestionnaire.send({ type: 'returnToDashboard' })
                }}
              >
                Close questionnaire
              </Button>
            ))
            .otherwise(() => (
              <Button
                size="l"
                variant="primary"
                trailingIcon={<ArrowRightIcon className="h-4 w-4" />}
                disabled={navState.next == undefined}
                onClick={() => stQuestionnaire.send({ type: 'navigateNext' })}
              >
                Next
              </Button>
            ))}
        </QuestionnaireFooter>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex w-full flex-col gap-2 py-2">
          {currentPage.title ? (
            <h1 className="text-center text-3xl font-bold">{currentPage.title}</h1>
          ) : null}

          {currentPage.subtitle && shouldShowSubtitle ? (
            <p className="text-center">
              <SimpleHtmlMarkdown content={currentPage.subtitle} />
            </p>
          ) : null}
        </div>

        {currentPage.components.map((i, index) => (
          <WizardUIComponent
            key={index}
            config={i}
            validationResult={questionnaireValidationResult}
          />
        ))}
      </div>

      {actionDialog}

      {failedUploads.length > 0 && (
        <Modal isOpen={true}>
          <UploadFailedDialog
            failedUpload={failedUploads[0]}
            onClose={() =>
              documentUploads.send({ type: 'clearUpload', uploadId: failedUploads[0].id })
            }
          />
        </Modal>
      )}
    </QuestionnairePageContainer>
  )
}

function UploadFailedDialog({
  failedUpload,
  onClose
}: {
  failedUpload: Upload
  onClose: () => void
}) {
  return (
    <Dialog
      title="Upload failed"
      subtitle={
        <div>
          <p>
            The file <span className="font-bold">{failedUpload.filename}</span> failed:{' '}
            {formatUploadError(failedUpload.error!)}.
          </p>
          <p>Please try to upload the file again.</p>
        </div>
      }
      buttons={
        <DialogButtons>
          <Button variant="primary" onClick={onClose}>
            OK
          </Button>
        </DialogButtons>
      }
    />
  )
}
